import { useState } from 'react';
import {
  Card, CardHeader, CardBody, Button, Field, ErrorNotice, PasswordInput } from '../../components/ui/index.js';
import { useMutation } from '../../hooks/useApi.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { http } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.jsx';
import { setAccessToken } from '../../lib/api.js';
import { formatExactTimestamp } from '../../lib/format.js';
import { ShieldCheck } from 'lucide-react';
import { PageHero } from '../../components/PageHero.jsx';

export function SecurityPage() {
  const toast = useToast();
  const { user, merchant } = useAuth();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [mismatch, setMismatch] = useState(null);

  const change = useMutation(async () => {
    if (form.newPassword !== form.confirmPassword) {
      setMismatch('Those passwords do not match.');
      return;
    }
    setMismatch(null);

    const result = await http.post('/auth/change-password', {
      currentPassword: form.currentPassword,
      newPassword: form.newPassword,
    });

    // MA-6: every other session is signed out. This one gets a fresh token so
    // the person changing their password is not logged out of the tab they did
    // it in — which would look like a failure.
    setAccessToken(result.accessToken);
    setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    toast.success('Password changed', { description: 'All your other sessions have been signed out.' });
  });

  const fieldErrors = change.error?.fieldErrors ?? {};
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <>
      <PageHero chip="Account" icon={ShieldCheck} title="Security" blurb="Your sign-in details and account information." />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Change password" blurb="Signs out every other session." />
          <CardBody>
            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); change.mutate().catch(() => {}); }}>
              {change.error && !Object.keys(fieldErrors).length && (
                <ErrorNotice error={change.error} title="Could not change your password" />
              )}

              <Field label="Current password" error={fieldErrors.currentPassword} required>
                {(props) => (
                  <PasswordInput {...props}  autoComplete="current-password"
                    value={form.currentPassword} onChange={set('currentPassword')} />
                )}
              </Field>

              <Field label="New password" hint="At least 10 characters." error={fieldErrors.newPassword} required>
                {(props) => (
                  <PasswordInput {...props}  autoComplete="new-password"
                    value={form.newPassword} onChange={set('newPassword')} />
                )}
              </Field>

              <Field label="Confirm new password" error={mismatch} required>
                {(props) => (
                  <PasswordInput {...props}  autoComplete="new-password" invalid={Boolean(mismatch)}
                    value={form.confirmPassword} onChange={set('confirmPassword')} />
                )}
              </Field>

              <Button type="submit" loading={change.loading}>Change password</Button>
            </form>
          </CardBody>
        </Card>

        <Card className="h-fit">
          <CardHeader title="Account" />
          <CardBody>
            <dl className="space-y-4">
              {[
                ['Business', merchant?.name],
                ['Email', user?.email],
                ['Account ID', merchant?.id],
                ['Last sign-in', user?.lastLoginAt ? formatExactTimestamp(user.lastLoginAt) : 'This is your first session'],
                ['Member since', merchant?.createdAt ? formatExactTimestamp(merchant.createdAt) : '—'],
              ].map(([term, value]) => (
                <div key={term}>
                  <dt className="text-xs font-medium uppercase tracking-wide text-muted">{term}</dt>
                  <dd className="mt-0.5 break-all text-sm text-ink">{value ?? '—'}</dd>
                </div>
              ))}
            </dl>
          </CardBody>
        </Card>
      </div>
    </>
  );
}

export default SecurityPage;
